import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, ArrowUpRight, Expand } from "lucide-react";
import { CATEGORIES, PROJECTS } from "../data/portfolio";
import { BeforeAfter } from "./BeforeAfter";
import { Lightbox } from "./Lightbox";
import { ProjectDetail } from "./ProjectDetail";

const ProjectHeader = ({ project, onView }) => (
  <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-5">
    <div>
      <div className="flex items-center gap-3 flex-wrap">
        <h3 className="font-display font-black uppercase tracking-tight text-[#1A1518] text-2xl md:text-3xl leading-tight">
          {project.title}
        </h3>
        {project.tag && (
          <span className="rounded-full brand-gradient text-white text-[10px] tracking-[0.25em] uppercase font-bold px-3 py-1">
            {project.tag}
          </span>
        )}
      </div>
      <div className="mt-2 flex items-center gap-2 text-[#4D454A] text-xs md:text-sm tracking-[0.2em] uppercase font-semibold">
        <MapPin size={14} className="text-[#c85a54]" />
        <span>{project.place}</span>
      </div>
    </div>
    <div className="hidden sm:block h-px flex-1 mx-6 bg-[#E5DEE5]" />
    <button
      data-testid={`view-project-${project.id}`}
      onClick={onView}
      className="group inline-flex items-center gap-2 text-[#c85a54] text-[11px] tracking-[0.3em] uppercase font-bold hover:text-[#4a1c40] transition-colors duration-200"
    >
      View Project
      <ArrowUpRight
        size={14}
        className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-200"
      />
    </button>
  </div>
);

const ClickableImage = ({ src, alt, aspect = "aspect-[4/3]", onClick, testId, rounded = "rounded-xl" }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    className={`group relative w-full overflow-hidden ${rounded} border border-[#E5DEE5] block`}
  >
    <img
      src={src}
      alt={alt}
      loading="lazy"
      className={`w-full h-full object-cover ${aspect} transition-transform duration-700 ease-out group-hover:scale-[1.08]`}
    />
    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
    <span className="absolute bottom-3 right-3 w-9 h-9 rounded-full bg-white/95 text-[#1A1518] flex items-center justify-center opacity-0 group-hover:opacity-100 group-hover:translate-y-0 translate-y-1 transition-all duration-300">
      <Expand size={16} />
    </span>
  </button>
);

const ProjectBlock = ({ project, span, onOpenLightbox, onOpenDetail }) => {
  const openLB = (startAt = 0) => onOpenLightbox(project.images || [], startAt);

  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6 }}
      data-testid={`project-${project.id}`}
      data-cursor="view"
      className={`${span} bg-white rounded-3xl border border-[#E5DEE5] p-5 md:p-8 shadow-sm hover:shadow-xl transition-shadow duration-500`}
    >
      <ProjectHeader project={project} onView={() => onOpenDetail(project)} />

      {project.type === "single" && (
        <ClickableImage
          src={project.src}
          alt={project.title}
          aspect="aspect-[4/3] md:aspect-[16/10]"
          rounded="rounded-2xl"
          testId={`img-${project.id}`}
          onClick={() => openLB(0)}
        />
      )}

      {project.type === "slider" && (
        <div className="space-y-5">
          {project.hero && (
            <ClickableImage
              src={project.hero}
              alt={`${project.title} render`}
              aspect="aspect-[16/10]"
              rounded="rounded-2xl"
              testId={`img-hero-${project.id}`}
              onClick={() => openLB(project.images.indexOf(project.hero))}
            />
          )}
          <BeforeAfter
            before={project.before}
            after={project.after}
            beforeLabel={project.beforeLabel}
            afterLabel={project.afterLabel}
            aspect="aspect-[16/10]"
            testId={`slider-${project.id}`}
          />
        </div>
      )}

      {project.type === "mixed" && (
        <div className="space-y-5">
          <BeforeAfter
            before={project.slider.before}
            after={project.slider.after}
            beforeLabel={project.slider.beforeLabel}
            afterLabel={project.slider.afterLabel}
            aspect="aspect-[16/10]"
            testId={`slider-${project.id}`}
          />
          <div className="text-[11px] tracking-[0.3em] uppercase text-[#8a828a] font-semibold pt-2">
            Interior Views
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
            {project.interiors.map((img, i) => (
              <ClickableImage
                key={i}
                src={img}
                alt={`${project.title} interior ${i + 1}`}
                aspect="aspect-[16/10]"
                testId={`img-int-${project.id}-${i}`}
                onClick={() => openLB(project.images.indexOf(img))}
              />
            ))}
          </div>
        </div>
      )}

      {project.type === "gallery" && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
          {project.images.map((img, i) => (
            <ClickableImage
              key={i}
              src={img}
              alt={`${project.title} ${i + 1}`}
              aspect="aspect-[4/3]"
              testId={`img-gal-${project.id}-${i}`}
              onClick={() => openLB(i)}
            />
          ))}
        </div>
      )}

      <p className="mt-5 text-[#4D454A] text-sm md:text-base leading-relaxed max-w-2xl">
        {project.caption}
      </p>
    </motion.article>
  );
};

const spanForType = (type) => {
  if (type === "single") return "lg:col-span-6 col-span-12";
  return "col-span-12";
};

export const Portfolio = () => {
  const [active, setActive] = useState("all");
  const [lightbox, setLightbox] = useState({ open: false, images: [], index: 0 });
  const [detail, setDetail] = useState(null);

  const items = active === "all" ? PROJECTS : PROJECTS.filter((p) => p.category === active);

  const openLightbox = (images, index) => setLightbox({ open: true, images, index });
  const closeLightbox = () => setLightbox({ open: false, images: [], index: 0 });

  return (
    <section id="portfolio" className="relative bg-[#FDFCFD] bg-grid-light">
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-24 md:py-32">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <div>
            <span className="text-[#c85a54] text-xs md:text-sm tracking-[0.3em] uppercase font-semibold">
              Portfolio
            </span>
            <h2 className="mt-5 font-display font-black uppercase tracking-tight leading-none text-[#1A1518] text-4xl md:text-6xl">
              Selected Works
            </h2>
            <p className="mt-5 text-[#4D454A] text-sm md:text-base max-w-xl leading-relaxed">
              Twelve projects, four disciplines. Tap any image to expand, or open a project for
              its brief, material palette, and full render set.
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            {CATEGORIES.map((c) => (
              <button
                key={c.key}
                data-testid={`filter-${c.key}`}
                onClick={() => setActive(c.key)}
                className={`rounded-full px-5 py-2.5 text-xs tracking-widest uppercase font-bold border transition-colors duration-300 ${
                  active === c.key
                    ? "brand-gradient text-white border-transparent"
                    : "bg-transparent text-[#4D454A] border-[#E5DEE5] hover:border-[#c85a54] hover:text-[#c85a54]"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        <div data-testid="portfolio-grid" className="mt-14 grid grid-cols-12 gap-6 md:gap-8">
          <AnimatePresence mode="popLayout">
            {items.map((p) => (
              <ProjectBlock
                key={p.id}
                project={p}
                span={spanForType(p.type)}
                onOpenLightbox={openLightbox}
                onOpenDetail={setDetail}
              />
            ))}
          </AnimatePresence>
        </div>
      </div>

      {lightbox.open && (
        <Lightbox
          images={lightbox.images}
          index={lightbox.index}
          onClose={closeLightbox}
          onNav={(i) => setLightbox((s) => ({ ...s, index: i }))}
        />
      )}

      {detail && <ProjectDetail project={detail} onClose={() => setDetail(null)} />}
    </section>
  );
};
